import { messaging, getToken } from '../firebaseConfig';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// VAPID key - you'll need to get this from Firebase Console > Project Settings > Cloud Messaging
// For now, using a placeholder - you'll need to replace this
const VAPID_KEY = "BC11iTLMOQJKRLS4QCQiknj0Kb10wc9Noqaz-vofsv7h_-cRSgVu77Cbeh980Gm01wZTuDuZrR6fP3D74kL3qYg"; // Replace with your actual VAPID key

/**
 * Validates if the VAPID key is properly configured
 * @returns {boolean} True if VAPID key is valid, false otherwise
 */
function isVAPIDKeyValid() {
  if (!VAPID_KEY || VAPID_KEY === "YOUR_VAPID_KEY_HERE") {
    return false;
  }
  // VAPID keys are base64url encoded and typically start with certain prefixes
  // A basic check: should be a long string (at least 80 characters)
  return VAPID_KEY.length > 80 && !VAPID_KEY.includes(' ');
}

/**
 * Request notification permission and get FCM token
 * @param {string} userId - The user's phone number or ID
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
export async function requestNotificationPermission(userId) {
  if (!messaging) {
    console.warn('Firebase Messaging is not available');
    return null;
  }

  // Check if VAPID key is configured
  if (!isVAPIDKeyValid()) {
    console.error(
      '⚠️ VAPID key is not configured!\n\n' +
      'To enable push notifications:\n' +
      '1. Go to Firebase Console > Project Settings > Cloud Messaging\n' +
      '2. Under "Web Push certificates", click "Generate key pair" (if needed)\n' +
      '3. Copy the "Key pair" value\n' +
      '4. Update VAPID_KEY in src/utils/notifications.js\n\n' +
      'See PUSH_NOTIFICATIONS_SETUP.md for detailed instructions.'
    );
    return null;
  }

  try {
    // Check current permission status
    let permission = Notification.permission;
    
    // Only request if permission hasn't been set yet
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    
    if (permission === 'granted') {
      console.log('Notification permission granted.');
      
      // Check if we already have a token stored
      const existingToken = await getStoredToken(userId);
      if (existingToken) {
        // Try to get a fresh token (in case it was refreshed)
        try {
          const token = await getToken(messaging, { vapidKey: VAPID_KEY });
          if (token && token !== existingToken) {
            // Token has changed, update it
            await saveTokenToFirestore(userId, token);
            console.log('FCM Token updated');
            return token;
          }
          // Token is the same, no need to update
          return existingToken;
        } catch (error) {
          // If error is about invalid VAPID key, provide helpful message
          if (error.message && error.message.includes('applicationServerKey')) {
            console.error(
              '⚠️ Invalid VAPID key!\n\n' +
              'The VAPID key must be a valid P-256 public key.\n' +
              'Please check that you copied the entire key from Firebase Console.\n' +
              'The key should be a long string (80+ characters) with no spaces.\n\n' +
              'See PUSH_NOTIFICATIONS_SETUP.md for instructions.'
            );
          } else {
            console.warn('Error getting fresh token, using stored token:', error);
          }
          return existingToken;
        }
      }
      
      // Get FCM token
      try {
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        
        if (token) {
          console.log('FCM Token:', token);
          
          // Store token in Firestore
          await saveTokenToFirestore(userId, token);
          
          return token;
        } else {
          console.warn('No FCM token available');
          return null;
        }
      } catch (error) {
        // If error is about invalid VAPID key, provide helpful message
        if (error.message && error.message.includes('applicationServerKey')) {
          console.error(
            '⚠️ Invalid VAPID key!\n\n' +
            'The VAPID key must be a valid P-256 public key.\n' +
            'Please check that you copied the entire key from Firebase Console.\n' +
            'The key should be a long string (80+ characters) with no spaces.\n\n' +
            'See PUSH_NOTIFICATIONS_SETUP.md for instructions.'
          );
        } else {
          console.error('Error getting FCM token:', error);
        }
        return null;
      }
    } else {
      console.log(`Notification permission ${permission}.`);
      return null;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
}

/**
 * Save FCM token to Firestore
 * @param {string} userId - The user's phone number or ID
 * @param {string} token - FCM token
 */
async function saveTokenToFirestore(userId, token) {
  try {
    const tokenRef = doc(db, 'fcmTokens', userId);
    await setDoc(tokenRef, {
      token: token,
      userId: userId,
      createdAt: new Date(),
      updatedAt: new Date()
    }, { merge: true });
    console.log('Token saved to Firestore');
  } catch (error) {
    console.error('Error saving token to Firestore:', error);
  }
}

/**
 * Get existing FCM token for a user
 * @param {string} userId - The user's phone number or ID
 * @returns {Promise<string|null>} FCM token or null
 */
export async function getStoredToken(userId) {
  try {
    const tokenRef = doc(db, 'fcmTokens', userId);
    const tokenSnap = await getDoc(tokenRef);
    
    if (tokenSnap.exists()) {
      return tokenSnap.data().token;
    }
    return null;
  } catch (error) {
    console.error('Error getting stored token:', error);
    return null;
  }
}

